import { useState } from "react";
import { exportFinetuneCsv, exportFinetuneCsvNoMidSilence, exportAnnotationsCsv, exportAnnotationsCsvWithEos, exportFilteredAnnotationsCsv } from "../services/api";
import Sidebar from "./Sidebar";
import { useEventLogger } from "../hooks/useEventLogger";

export default function DataFormation({ user, onPageChange }) {
  const { logNavigation, logLogout } = useEventLogger(user);
  const [loading, setLoading] = useState(false);
  const [loadingClean, setLoadingClean] = useState(false);
  const [loadingAnnotations, setLoadingAnnotations] = useState(false);
  const [loadingAnnotationsEos, setLoadingAnnotationsEos] = useState(false);
  const [loadingFiltered, setLoadingFiltered] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Filtered export state
  const [filterAudioType, setFilterAudioType] = useState(null);
  const [filterInteractionType, setFilterInteractionType] = useState(null);
  const [filterQualityRating, setFilterQualityRating] = useState(null);
  const [filterFluency, setFilterFluency] = useState(null);

  const fluencyOptions = [
    { value: 1, label: "Very hesitant, many pauses, hard to follow" },
    { value: 2, label: "Frequent pauses, slow, several restarts" },
    { value: 3, label: "Understandable, some pauses/restarts" },
    { value: 4, label: "Smooth, minor pauses" },
    { value: 5, label: "Very smooth, natural pace" },
  ];
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");

  const today = new Date().toISOString().split("T")[0];

  const handleExportCsv = async () => {
    setLoading(true);
    setMessage("");
    setError("");
    try {
      await exportFinetuneCsv({ startDate: startDate || undefined, endDate: endDate || undefined });
      setMessage("CSV generation started! The file will be uploaded to the 'finetune-data-csv/all/' S3 folder once complete.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start CSV generation.");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCleanCsv = async () => {
    setLoadingClean(true);
    setMessage("");
    setError("");
    try {
      await exportFinetuneCsvNoMidSilence({ startDate: startDate || undefined, endDate: endDate || undefined });
      setMessage("Clean CSV generation started! The file will be uploaded to the 'finetune-data-csv/no_mid_silence/' S3 folder once complete.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start CSV generation.");
    } finally {
      setLoadingClean(false);
    }
  };

  const handleExportAnnotationsCsv = async () => {
    setLoadingAnnotations(true);
    setMessage("");
    setError("");
    try {
      await exportAnnotationsCsv({ startDate: startDate || undefined, endDate: endDate || undefined });
      setMessage("Annotations CSV generation started! The file will be uploaded to the 'finetune-data-csv/annotations/' S3 folder once complete.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start annotations CSV generation.");
    } finally {
      setLoadingAnnotations(false);
    }
  };

  const handleExportFilteredCsv = async () => {
    setLoadingFiltered(true);
    setMessage("");
    setError("");
    try {
      await exportFilteredAnnotationsCsv({
        audioType: filterAudioType || undefined,
        interactionType: filterInteractionType || undefined,
        qualityRating: filterQualityRating != null ? filterQualityRating : undefined,
        fluency: filterFluency || undefined,
        startDate: filterStartDate || undefined,
        endDate: filterEndDate || undefined,
      });
      setMessage("Filtered annotations CSV generation started! The file will be uploaded to 'finetune-data-csv/annotations_data/' once complete.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start filtered CSV generation.");
    } finally {
      setLoadingFiltered(false);
    }
  };

  const handleExportAnnotationsCsvWithEos = async () => {
    setLoadingAnnotationsEos(true);
    setMessage("");
    setError("");
    try {
      await exportAnnotationsCsvWithEos({ startDate: startDate || undefined, endDate: endDate || undefined });
      setMessage("Annotations EOS CSV generation started! The file will be uploaded to the 'finetune-data-csv/annotations_eos/' S3 folder once complete.");
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start annotations EOS CSV generation.");
    } finally {
      setLoadingAnnotationsEos(false);
    }
  };

  return (
    <div className="workspace">
      <Sidebar
        user={user}
        currentPage="data-formation"
        onPageChange={onPageChange}
        onLogout={logLogout}
        onNavigation={logNavigation}
      />
      <main className="annotator">
        <div className="user-management">
          <h1>Data Formation</h1>

          {error && <div className="error">{error}</div>}
          {message && <div style={{ marginBottom: "16px", color: "green" }}>{message}</div>}

          <div className="user-section">
            <h2>Finetune CSV Export</h2>
            <p>Generates a CSV from all processed audio chunks and uploads it to S3 for finetuning. This runs in the background and may take several minutes.</p>

            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={{ fontSize: "13px", color: "#555" }}>Start Date</label>
                <input
                  type="date"
                  value={startDate}
                  max={endDate || today}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="date-select"
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={{ fontSize: "13px", color: "#555" }}>End Date</label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  max={today}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="date-select"
                />
              </div>
              {(startDate || endDate) && (
                <button
                  onClick={() => { setStartDate(""); setEndDate(""); }}
                  style={{ alignSelf: "flex-end", background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "13px" }}
                >
                  Clear
                </button>
              )}
            </div>

            <button onClick={handleExportCsv} disabled={loading}>
              {loading ? "Starting..." : "Form csv and upload data for finetuning"}
            </button>
            <button onClick={handleExportCleanCsv} disabled={loadingClean} style={{ marginLeft: "12px" }}>
              {loadingClean ? "Starting..." : "Export clean audio csv (no mid-silence)"}
            </button>
            <button onClick={handleExportAnnotationsCsv} disabled={loadingAnnotations} style={{ marginLeft: "12px" }}>
              {loadingAnnotations ? "Starting..." : "Export annotations csv"}
            </button>
            <button onClick={handleExportAnnotationsCsvWithEos} disabled={loadingAnnotationsEos} style={{ marginLeft: "12px" }}>
              {loadingAnnotationsEos ? "Starting..." : "Export annotations csv (with EOS)"}
            </button>
            </div>

          <div className="user-section">
            <h2>Filtered Annotations CSV Export</h2>
            <p>Filter annotations by audio type, interaction type, and quality rating, then export a CSV with S3 URLs to the <code>annotations_data</code> folder.</p>

            {/* Audio Type */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "8px" }}>Audio Type</label>
              <div className="chip-row">
                {["noise", "silence", "child", "adult"].map((type) => (
                  <button
                    key={type}
                    className={`chip${filterAudioType === type ? " active" : ""}`}
                    onClick={() => {
                      setFilterAudioType(filterAudioType === type ? null : type);
                      setFilterInteractionType(null);
                      setFilterQualityRating(null);
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {/* Interaction Type (child or adult only) */}
            {(filterAudioType === "child" || filterAudioType === "adult") && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "8px" }}>Interaction Type</label>
                <div className="chip-row">
                  {(filterAudioType === "child"
                    ? ["to_doro", "to_parent", "background"]
                    : ["to_doro", "to_child", "background"]
                  ).map((type) => (
                    <button
                      key={type}
                      className={`chip${filterInteractionType === type ? " active" : ""}`}
                      onClick={() => {
                        setFilterInteractionType(filterInteractionType === type ? null : type);
                        setFilterQualityRating(null);
                        setFilterFluency(null);
                      }}
                    >
                      {type}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quality Rating (to_doro only) */}
            {filterInteractionType === "to_doro" && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "8px" }}>
                  Quality Rating{filterQualityRating != null ? ` (${filterQualityRating} selected)` : " (any)"}
                </label>
                <div className="chip-row">
                  {[1,2,3,4,5,6,7,8,9,10].map((num) => (
                    <button
                      key={num}
                      className={`chip${filterQualityRating === num ? " active" : ""}`}
                      onClick={() => setFilterQualityRating(filterQualityRating === num ? null : num)}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Fluency (to_doro only) */}
            {filterInteractionType === "to_doro" && (
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontSize: "13px", color: "#555", display: "block", marginBottom: "8px" }}>
                  Fluency{filterFluency ? ` (${fluencyOptions.find(o => o.label === filterFluency)?.value} selected)` : " (any)"}
                </label>
                <div className="chip-row">
                  {fluencyOptions.map((opt) => (
                    <button
                      key={opt.value}
                      className={`chip${filterFluency === opt.label ? " active" : ""}`}
                      onClick={() => setFilterFluency(filterFluency === opt.label ? null : opt.label)}
                    >
                      {opt.value} — {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Date range */}
            <div style={{ display: "flex", alignItems: "center", gap: "16px", marginBottom: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={{ fontSize: "13px", color: "#555" }}>Start Date</label>
                <input
                  type="date"
                  value={filterStartDate}
                  max={filterEndDate || today}
                  onChange={(e) => setFilterStartDate(e.target.value)}
                  className="date-select"
                />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <label style={{ fontSize: "13px", color: "#555" }}>End Date</label>
                <input
                  type="date"
                  value={filterEndDate}
                  min={filterStartDate || undefined}
                  max={today}
                  onChange={(e) => setFilterEndDate(e.target.value)}
                  className="date-select"
                />
              </div>
              {(filterStartDate || filterEndDate) && (
                <button
                  onClick={() => { setFilterStartDate(""); setFilterEndDate(""); }}
                  style={{ alignSelf: "flex-end", background: "none", border: "none", color: "#888", cursor: "pointer", fontSize: "13px" }}
                >
                  Clear
                </button>
              )}
            </div>

            {/* Active filters summary */}
            {(filterAudioType || filterInteractionType || filterQualityRating != null || filterFluency || filterStartDate || filterEndDate) && (
              <div style={{ display: "flex", alignItems: "center", gap: "12px", fontSize: "13px", color: "#555", marginBottom: "12px" }}>
                <span>
                  Filters: {[
                    filterAudioType && `audio_type = ${filterAudioType}`,
                    filterInteractionType && `interaction_type = ${filterInteractionType}`,
                    filterQualityRating != null && `quality_rating = ${filterQualityRating}`,
                    filterFluency && `fluency = ${fluencyOptions.find(o => o.label === filterFluency)?.value} (${filterFluency})`,
                    filterStartDate && `from = ${filterStartDate}`,
                    filterEndDate && `to = ${filterEndDate}`,
                  ].filter(Boolean).join(" · ")}
                </span>
                <button
                  onClick={() => {
                    setFilterAudioType(null);
                    setFilterInteractionType(null);
                    setFilterQualityRating(null);
                    setFilterFluency(null);
                    setFilterStartDate("");
                    setFilterEndDate("");
                  }}
                  style={{ background: "none", border: "1px solid #ccc", borderRadius: "4px", color: "#888", cursor: "pointer", fontSize: "12px", padding: "2px 8px" }}
                >
                  Clear All
                </button>
              </div>
            )}

            <button onClick={handleExportFilteredCsv} disabled={loadingFiltered}>
              {loadingFiltered ? "Starting..." : "Save CSV to annotations_data"}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
